<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Manpower Management</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Last updated: {{ currentDate }}</p>
          </div>
          <div class="flex gap-3">
            <button 
              @click="refreshData"
              class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" :class="{'animate-spin': isRefreshing}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            <button 
              v-if="hasPermission('manpower', 'add_applicant')"
              @click="showAddForm = !showAddForm"
              class="inline-flex items-center px-4 py-2 bg-[#74bd54] hover:bg-[#63a43f] text-white rounded-lg text-sm font-medium shadow-sm shadow-[#74bd54]/30 transition-all active:scale-95"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ showAddForm ? 'Close Form' : 'New Applicant' }}
            </button>
          </div>
        </div>

        <!-- Add/Edit Applicant Form (Collapsible) -->
        <Transition name="slide-fade">
          <div v-if="showAddForm" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8">
            <div class="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-[#035a76]/10 dark:bg-[#035a76]/30 rounded-lg flex items-center justify-center text-[#035a76] dark:text-blue-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ isEditMode ? 'Edit Applicant' : 'Add New Applicant' }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Fill in the details below to register or update a candidate.</p>
                </div>
              </div>
              <button @click="cancelEdit" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form @submit.prevent="addApplicant" class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Full Name -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Full Name *</label>
                <input v-model="newApplicant.name" type="text" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" placeholder="Enter full name" />
              </div>

              <!-- Position & Email -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Position Applied For *</label>
                <select v-model="newApplicant.position_id" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition">
                  <option disabled value="">Select Position</option>
                  <option v-for="pos in positions" :key="pos.id" :value="pos.id">{{ pos.position_name }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Address *</label>
                <input v-model="newApplicant.email" type="email" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" placeholder="example@email.com" />
              </div>

              <!-- Phone & Status -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Phone Number *</label>
                <input v-model="newApplicant.phone" type="tel" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" placeholder="+233 XX XXX XXXX" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Current Status *</label>
                <select v-model="newApplicant.status" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition">
                  <option disabled value="">Select Status</option>
                  <option>Shortlisted</option>
                  <option>Processing</option>
                  <option>Interviewed</option>
                  <option>Validated</option>
                  <option>Contracting</option>
                  <option>Hired</option>
                  <option>Rejected</option>
                  <option>Kept</option>
                </select>
              </div>

              <!-- Entry Type & Period -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Applicant Type *</label>
                <select v-model="newApplicant.entry_status" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition">
                  <option disabled value="">Select Type</option>
                  <option value="Old">Old</option>
                  <option value="New">New</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Period *</label>
                <select v-model="newApplicant.period" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition">
                  <option disabled value="">Select Period</option>
                  <option value="First Term">First Term</option>
                  <option value="Second Term">Second Term</option>
                  <option value="Third Term">Third Term</option>
                </select>
              </div>

              <!-- Interview Date & Time (New applicants only) -->
              <div v-if="newApplicant.entry_status === 'New'" class="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                <div>
                  <label class="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-1.5">Interview Date *</label>
                  <input v-model="newApplicant.interview_date" type="date" required class="w-full border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-1.5">Interview Time *</label>
                  <input v-model="newApplicant.interview_time" type="time" required class="w-full border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
              </div>

              <!-- Recommendation Source -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Recommendation Source</label>
                <select v-model="newApplicant.recommendation" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition">
                  <option disabled value="">Select Recommendation</option>
                  <option value="agency">Agency</option>
                  <option value="inward">Inward (Staff Referral)</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <!-- Agency Select -->
              <div v-if="newApplicant.recommendation === 'agency'" class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Select Agency</label>
                <select v-model="newApplicant.agency_id" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition">
                  <option disabled value="">Select Agency</option>
                  <option v-for="agency in agencies" :key="agency.id" :value="agency.id">{{ agency.name }}</option>
                </select>
              </div>

              <!-- Inward Staff Multiselect -->
              <div v-if="newApplicant.recommendation === 'inward'" class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Referred By Staff</label>
                <Multiselect v-model="selectedStaff" :options="staffMembers" label="applicant_name" track-by="id" placeholder="Search and select staff member" class="w-full" />
              </div>

              <!-- CV Submission Section -->
              <div class="md:col-span-2 bg-gray-50 dark:bg-gray-900/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">CV Submission Method</label>
                <select v-model="newApplicant.file_type" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] mb-4">
                  <option disabled value="">Choose how you want to submit the CV</option>
                  <option value="file">📄 Upload PDF File</option>
                  <option value="photo">📸 Take Photos of CV Pages (Live Camera)</option>
                </select>

                <!-- File Upload -->
                <div v-if="newApplicant.file_type === 'file'" class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-[#74bd54] hover:bg-[#74bd54]/5 transition-colors">
                  <input type="file" ref="cvInput" @change="handleFileUpload" accept=".pdf" class="hidden" id="cv-upload" />
                  <label for="cv-upload" class="cursor-pointer flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span class="font-medium text-gray-700 dark:text-gray-200">Click to upload PDF</span>
                    <span class="text-xs text-gray-500 mt-1">Only PDF files accepted</span>
                  </label>
                </div>

                <!-- Live Camera Section -->
                <div v-if="newApplicant.file_type === 'photo'" class="space-y-4">
                  <div class="relative bg-black rounded-xl overflow-hidden aspect-video border border-gray-300 dark:border-gray-600">
                    <video ref="videoRef" autoplay playsinline class="w-full h-full object-cover"></video>
                    <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div class="border-2 border-white/50 border-dashed w-[85%] h-[85%] rounded-lg flex items-center justify-center">
                        <span class="text-white/70 text-xs bg-black/50 px-3 py-1 rounded backdrop-blur-sm">Position CV here</span>
                      </div>
                    </div>
                    <button type="button" @click="capturePhoto" class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white text-gray-900 w-14 h-14 rounded-full flex items-center justify-center shadow-xl active:scale-95 transition-transform border-4 border-gray-800">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    <button type="button" @click="switchCamera" class="absolute top-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 backdrop-blur-sm hover:bg-black/80 transition">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Flip
                    </button>
                  </div>

                  <!-- Captured Photos Preview -->
                  <div v-if="capturedPhotos.length > 0" class="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    <div v-for="(photo, index) in capturedPhotos" :key="index" class="relative group">
                      <img :src="photo" class="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600" />
                      <button type="button" @click="removePhoto(index)" class="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow hover:bg-red-600 text-xs">✕</button>
                    </div>
                  </div>
                </div>

                <!-- File Preview -->
                <div v-if="newApplicant.cv" class="mt-4 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ newApplicant.cv.name }}</p>
                      <p class="text-xs text-gray-500">{{ (newApplicant.cv.size / 1024).toFixed(1) }} KB</p>
                    </div>
                  </div>
                  <button type="button" @click="newApplicant.cv = null" class="text-red-500 hover:text-red-600 text-sm font-medium">Remove</button>
                </div>
              </div>

              <!-- Comments -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Additional Comments / Notes</label>
                <textarea v-model="newApplicant.comment" rows="3" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition resize-none" placeholder="Enter any additional information..."></textarea>
              </div>

              <!-- ========================================== -->
              <!-- PRE-INTERVIEW ATTACHMENTS (EDIT MODE ONLY) -->
              <!-- ========================================== -->
              <div v-if="isEditMode" class="md:col-span-2 bg-gray-50 dark:bg-gray-900/50 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                <div class="flex items-center gap-3 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#035a76] dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Pre-Interview Attachments</h3>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Documents filled before interview (ID, Forms, Certificates, etc.)</p>
                  </div>
                </div>

                <!-- Attachment Type Selection -->
                <select v-model="newApplicant.attachment_type" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] mb-4">
                  <option disabled value="">Choose attachment method</option>
                  <option value="file">📄 Upload Files (PDF, JPG, PNG)</option>
                  <option value="photo">📸 Take Photos (Live Camera)</option>
                </select>

                <!-- File Upload -->
                <div v-if="newApplicant.attachment_type === 'file'" class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-[#74bd54] hover:bg-[#74bd54]/5 transition-colors">
                  <input type="file" ref="attachmentInput" @change="handleAttachmentUpload" accept=".pdf,.jpg,.jpeg,.png" class="hidden" id="attachment-upload" multiple />
                  <label for="attachment-upload" class="cursor-pointer flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span class="font-medium text-gray-700 dark:text-gray-200">Click to upload files</span>
                    <span class="text-xs text-gray-500 mt-1">PDF, JPG, PNG accepted</span>
                  </label>
                </div>

                <!-- Live Camera Section for Attachments -->
                <div v-if="newApplicant.attachment_type === 'photo'" class="space-y-4">
                  <div class="text-sm font-medium text-gray-700 dark:text-gray-300">Live Camera Preview</div>
                  <div class="relative bg-black rounded-xl overflow-hidden aspect-video border border-gray-300 dark:border-gray-600">
                    <video ref="attachmentVideoRef" autoplay playsinline class="w-full h-full object-cover"></video>
                    <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div class="border-2 border-white/50 border-dashed w-[85%] h-[85%] rounded-lg flex items-center justify-center">
                        <span class="text-white/70 text-xs bg-black/50 px-3 py-1 rounded backdrop-blur-sm">Position document here</span>
                      </div>
                    </div>
                    <button type="button" @click="captureAttachmentPhoto" class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white text-gray-900 w-14 h-14 rounded-full flex items-center justify-center shadow-xl active:scale-95 transition-transform border-4 border-gray-800">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    <button type="button" @click="switchAttachmentCamera" class="absolute top-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 backdrop-blur-sm hover:bg-black/80 transition">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Flip
                    </button>
                  </div>

                  <!-- Captured Attachment Photos Preview -->
                  <div v-if="attachmentPhotos.length > 0" class="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    <div v-for="(photo, index) in attachmentPhotos" :key="index" class="relative group">
                      <img :src="photo" class="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600" />
                      <button type="button" @click="removeAttachmentPhoto(index)" class="absolute -top-2 -right-2 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center shadow hover:bg-red-600 text-xs">✕</button>
                    </div>
                  </div>
                  <p class="text-xs text-amber-600 dark:text-amber-400 text-center">Take photos of all pre-interview documents (ID, Forms, Certificates, etc.)</p>
                </div>

                <!-- Existing Attachments Display -->
                <div v-if="existingAttachments && existingAttachments.length > 0" class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Existing Attachments ({{ existingAttachments.length }})</p>
                  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    <div v-for="(att, index) in existingAttachments" :key="index" class="relative group">
                      <a :href="att.file_path.startsWith('http') ? att.file_path : backendURL + att.file_path" target="_blank" class="block p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-[#035a76] dark:hover:border-blue-500 transition text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mx-auto text-gray-500 dark:text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p class="text-xs text-gray-600 dark:text-gray-400 truncate">{{ att.file_path.split('/').pop() }}</p>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <!-- ========================================== -->

              <!-- Submit Button -->
              <div class="md:col-span-2 flex justify-end pt-2">
                <button type="submit" :disabled="isLoading" class="inline-flex items-center px-6 py-3 bg-[#74bd54] hover:bg-[#63a43f] disabled:opacity-70 disabled:cursor-not-allowed text-white rounded-lg font-medium shadow-sm shadow-[#74bd54]/30 transition-all active:scale-95">
                  <svg v-if="isLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isLoading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Applicant' : 'Add Applicant') }}
                </button>
              </div>
            </form>
          </div>
        </Transition>

        <!-- Stats Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Applicants</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ applicants.length }}</p>
              </div>
              <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">All recorded applicants</p>
          </div>

          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Hired</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ countStatus('Hired') }}</p>
              </div>
              <div class="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Successfully hired applicants</p>
          </div>

          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Rejected</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ countStatus('Rejected') }}</p>
              </div>
              <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Rejected applicants</p>
          </div>

          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Kept / On Hold</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ countStatus('Kept') }}</p>
              </div>
              <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Applicants on hold</p>
          </div>
        </div>

        <!-- Filter Section -->
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700 p-6 mb-8">
          <div class="flex justify-between items-center mb-6">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Filter Applicants</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">Narrow down the list using the criteria below</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Status</label>
              <select v-model="filters.status" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Status</option>
                <option v-for="tab in tabs" :key="tab" :value="tab">{{ tab }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Position</label>
              <select v-model="filters.position" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Positions</option>
                <option v-for="pos in positions" :key="pos.id" :value="pos.position_name">{{ pos.position_name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Period</label>
              <select v-model="filters.period" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Terms</option>
                <option v-for="p in periods" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Month</label>
              <select v-model="selectedMonth" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Months</option>
                <option v-for="m in months" :key="m.value" :value="m.value">{{ m.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Year</label>
              <select v-model="selectedYear" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Years</option>
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Search</label>
              <div class="relative">
                <input v-model="filters.search" type="text" placeholder="Name or position..." class="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition" />
                <svg class="w-4 h-4 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-4">
            <div class="text-sm text-gray-500 dark:text-gray-400">
              Showing <span class="font-semibold text-gray-900 dark:text-white">{{ filteredApplicants.length }}</span> results
            </div>
            <div class="flex space-x-3 w-full sm:w-auto">
              <button @click="resetFilters" class="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Reset</button>
              <button @click="applyFilters" class="flex-1 sm:flex-none px-6 py-2 rounded-lg bg-[#74bd54] text-white shadow-sm hover:bg-[#65a848] transition font-medium text-sm">Apply Filters</button>
            </div>
          </div>
        </div>

        <!-- Applicants Table -->
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">CV</th>
                  <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                <tr v-for="applicant in filteredApplicants" :key="applicant.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ applicant.name }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">{{ applicant.email }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ applicant.position }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getStatusColor(applicant.status)" class="px-2.5 py-1 rounded-full text-xs font-semibold capitalize">
                      {{ applicant.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ applicant.period }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(applicant.date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex flex-col gap-1">
                      <a v-if="applicant.cv" :href="backendURL + applicant.cv" target="_blank" class="inline-flex items-center text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        View PDF
                      </a>
                      <button v-if="applicant.cv_photo_paths?.length > 0" @click="viewPhotos(applicant)" class="inline-flex items-center text-xs font-medium text-green-600 dark:text-green-400 hover:underline">
                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Photos ({{ applicant.cv_photo_paths.length }})
                      </button>
                      <span v-if="!applicant.cv && !applicant.cv_photo_paths?.length" class="text-xs text-gray-400">No CV</span>
                    </div>
                  </td>
                  
                  <!-- Professional Actions Dropdown -->
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div class="relative inline-block text-left" @click.stop>
                      <button @click="toggleActionMenu(applicant.id === activeActionMenu ? null : applicant.id)" class="inline-flex items-center p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" /></svg>
                      </button>
                      
                      <div v-if="activeActionMenu === applicant.id" class="absolute right-0 z-20 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 dark:divide-gray-700 origin-top-right">
                        <div class="py-1">
                          <router-link v-if="hasPermission('manpower', 'interview')" :to="`/interview/${applicant.id}`" @click="activeActionMenu = null" class="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <svg class="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            View / Interview
                          </router-link>
                          <button v-if="hasPermission('manpower', 'edit')" @click="editApplicant(applicant); activeActionMenu = null" class="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <svg class="w-4 h-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            Edit Details
                          </button>
                        </div>
                        <div class="py-1">
                          <button v-if="hasPermission('manpower', 'validate')" @click="openActionModal(applicant, 'Validated'); activeActionMenu = null" class="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <svg class="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Validate
                          </button>
                          <button v-if="hasPermission('manpower', 'process')" @click="openActionModal(applicant, 'Processing'); activeActionMenu = null" class="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <svg class="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                            Process
                          </button>
                          <button v-if="hasPermission('manpower', 'keep')" @click="openActionModal(applicant, 'Kept'); activeActionMenu = null" class="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <svg class="w-4 h-4 mr-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Keep / Hold
                          </button>
                          <button v-if="hasPermission('manpower', 'reject')" @click="openActionModal(applicant, 'Rejected'); activeActionMenu = null" class="flex w-full items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            Reject
                          </button>
                        </div>
                        <div class="py-1">
                          <button v-if="hasPermission('manpower', 'delete_applicant')" @click="deleteApplicant(applicant); activeActionMenu = null" class="flex w-full items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Delete Applicant
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                
                <!-- Empty State -->
                <tr v-if="filteredApplicants.length === 0">
                  <td colspan="7" class="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="text-sm font-medium">No applicants found matching your filters.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modals (Action, View, Photos) -->
        <Teleport to="body">
          <ActionModal v-if="showActionModal" :applicant="selectedApplicant" :initialStatus="actionStatus" @close="showActionModal = false" @save="updateApplicantStatus" />
          <ViewModal v-if="showViewModal" :applicant="selectedApplicant" @close="showViewModal = false" />
          
          <!-- Photos Modal -->
          <div v-if="showPhotosModal" class="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" @click.self="closePhotosModal">
            <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div class="flex justify-between items-center p-6 border-b dark:border-gray-700">
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">CV Photos - {{ modalApplicant?.name }}</h3>
                <button @click="closePhotosModal" class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div class="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div v-for="(photo, index) in modalApplicant?.cv_photo_paths" :key="index" class="relative group cursor-pointer" @click="viewFullImage(photo)">
                  <img :src="photo.startsWith('http') ? photo : backendURL + photo" class="w-full h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600" />
                  <div class="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all rounded-lg flex items-center justify-center">
                    <span class="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">🔍 Click to enlarge</span>
                  </div>
                </div>
              </div>
              <div class="p-6 border-t dark:border-gray-700 flex justify-end">
                <button @click="closePhotosModal" class="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">Close</button>
              </div>
            </div>
          </div>
          
          <!-- Full Image Viewer -->
          <div v-if="fullImage" class="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" @click="closeFullImage">
            <img :src="fullImage" class="max-w-full max-h-full object-contain rounded-lg" @click.stop />
            <button @click="closeFullImage" class="absolute top-4 right-4 text-white hover:text-gray-300">
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </Teleport>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import api from "@/services/api";
import HrSidebar from '@/components/hr/HrSidebar.vue';
import HrTopbar from '@/components/hr/HrTopbar.vue';
import ActionModal from '@/components/hr/ActionModal.vue';
import ViewModal from '@/components/hr/ViewModal.vue';
import { usePermissions } from '@/composables/usePermissions';
import { useToast } from 'vue-toastification';

const { hasPermission } = usePermissions();
const toast = useToast();

const tabs = ['All', 'Hired', 'Rejected', 'Kept', 'Processing', 'Interviewed', 'Shortlisted', 'Validated'];
const isSidebarOpen = ref(false);
const isDarkMode = ref(false);
const isRefreshing = ref(false);
const currentPageTitle = ref('Manpower Management');
const showActionModal = ref(false);
const showViewModal = ref(false);
const showAddForm = ref(false);
const selectedApplicant = ref(null);
const actionStatus = ref('');
const activeActionMenu = ref(null);

const positions = ref([]);
const applicants = ref([]);
const agencies = ref([]);
const staffMembers = ref([]);
const selectedStaff = ref(null);
const isEditMode = ref(false);       
const editingApplicantId = ref(null);

// CV Camera refs
const videoRef = ref(null);
const currentStream = ref(null);
const capturedPhotos = ref([]);
const facingMode = ref('environment');

// Attachment Camera refs (RESTORED)
const attachmentInput = ref(null);
const attachmentVideoRef = ref(null);
const attachmentStream = ref(null);
const attachmentPhotos = ref([]);
const attachmentPhotoFiles = ref([]);
const attachmentFacingMode = ref('environment');
const existingAttachments = ref([]);

const showPhotosModal = ref(false);
const fullImage = ref(null);
const modalApplicant = ref(null); 

const filters = ref({ status: '', position: '', period: '', date: '', search: '' });
const periods = ref(['First Term', 'Second Term', 'Third Term']);
const selectedMonth = ref('');
const selectedYear = ref('');
const months = [
  { value: '01', name: 'January' }, { value: '02', name: 'February' }, { value: '03', name: 'March' },
  { value: '04', name: 'April' }, { value: '05', name: 'May' }, { value: '06', name: 'June' },
  { value: '07', name: 'July' }, { value: '08', name: 'August' }, { value: '09', name: 'September' },
  { value: '10', name: 'October' }, { value: '11', name: 'November' }, { value: '12', name: 'December' }
];

const years = computed(() => {
  const unique = new Set();
  applicants.value.forEach(app => {
    if (app.date) unique.add(app.date.split('-')[0]);
  });
  return Array.from(unique).sort().reverse();
});

const currentDate = computed(() => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
const backendURL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/";

// --- Helpers ---
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStatusColor = (status) => {
  const map = {
    'Shortlisted': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    'Processing': 'bg-blue-200 text-blue-900 dark:bg-blue-800/30 dark:text-blue-300',
    'Interviewed': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    'Validated': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    'Contracting': 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-400',
    'Hired': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'Rejected': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'Kept': 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return map[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const toggleActionMenu = (id) => { activeActionMenu.value = id; };

const handleClickOutside = (event) => {
  if (!event.target.closest('.relative.inline-block')) {
    activeActionMenu.value = null;
  }
};

// --- API & Data ---
const fetchPositions = async () => {
  try {
    const response = await api.get("Positions.php");
    positions.value = response.data.map(position => ({ id: position.id, position_name: position.title }));
  } catch (error) { console.error("Error fetching Positions:", error); }
};

const fetchAgencies = async () => {
  try {
    const response = await api.get("Agencies.php");
    agencies.value = response.data.map(agency => ({ id: agency.id, name: agency.name }));
  } catch (error) { console.error("Error fetching Agencies:", error); }
};

const fetchApplicants = async () => {
  try {
    const response = await api.get("Applicants.php");
    applicants.value = response.data.map(applicant => ({
      id: applicant.id, name: applicant.full_name, email: applicant.email || '',
      phone: applicant.phone || '', position_id: applicant.position_id || '',
      position: applicant.position_title || '', status: applicant.applicant_status || '',
      entry_status: applicant.entry_status || '', period: applicant.period || '',
      comment: applicant.applicant_comment || '', date: applicant.created_at || '',
      cv: applicant.cv_file_path || '', recommendation: applicant.recommendation_type || '',
      agency_id: applicant.recommendation_type === 'agency' ? applicant.recommendation_id : '',
      inward_id: applicant.recommendation_type === 'inward' ? applicant.recommendation_id : '',
      interview_date: applicant.interview_date ? applicant.interview_date.split(' ')[0] : '',
      interview_time: applicant.interview_time?.substring(0, 5) || '',
      cv_type: applicant.cv_type || 'file',
      cv_photo_paths: applicant.cv_photo_paths || [],
      attachments: applicant.attachments || []
    }));
  } catch (error) { console.error("Error fetching Applicants:", error); }
};

const fetchInward = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');
    staffMembers.value = response.data.data.map(item => ({
      id: item.applicant_id, applicant_name: item.full_name,
      position_title: item.position?.position_title
    }));
  } catch (error) { console.error('Error fetching staff members:', error); }
};

const filteredApplicants = computed(() => {
  return applicants.value.filter(applicant => {
    const matchMonth = !selectedMonth.value || applicant.date?.split('-')[1] === selectedMonth.value;
    const matchYear = !selectedYear.value || applicant.date?.split('-')[0] === selectedYear.value;
    const matchPeriod = !filters.value.period || applicant.period === filters.value.period;
    const matchPosition = !filters.value.position || applicant.position === filters.value.position;
    const matchSearch = !filters.value.search || applicant.name.toLowerCase().includes(filters.value.search.toLowerCase()) || applicant.email.toLowerCase().includes(filters.value.search.toLowerCase());
    const matchStatus = !filters.value.status || applicant.status === filters.value.status;
    return matchMonth && matchYear && matchPeriod && matchPosition && matchSearch && matchStatus;
  });
});

const countStatus = (status) => applicants.value.filter(a => a.status === status).length;

// --- Actions ---
const resetFilters = () => {
  filters.value = { status: '', position: '', period: '', date: '', search: '' };
  selectedMonth.value = '';
  selectedYear.value = '';
};

const applyFilters = () => { /* Computed property handles this automatically */ };

const refreshData = () => {
  isRefreshing.value = true;
  setTimeout(() => { fetchApplicants(); isRefreshing.value = false; }, 800);
};

const newApplicant = ref({
  name: '', position_id: '', email: '', phone: '', status: '', entry_status: '',
  period: '', recommendation: '', agency_id: '', inward_id: '', file_type: '',
  cv: null, cv_photos: [], cv_photo_files: [], comment: '', interview_date: '',
  interview_time: '', attachment_type: '', attachment_files: [], attachment_photo_files: []
});

const handleFileUpload = (e) => {
  const file = e.target.files[0];
  if (file) newApplicant.value.cv = file;  
};

// --- ATTACHMENT FUNCTIONS (RESTORED) ---
const handleAttachmentUpload = (e) => {
  const files = e.target.files;
  if (files && files.length > 0) {
    for (let i = 0; i < files.length; i++) {
      attachmentPhotoFiles.value.push(files[i]);
      newApplicant.value.attachment_files.push(files[i]);
    }
  }
};

async function startAttachmentCamera() {
  stopAttachmentCamera();
  try {
    const constraints = { video: { facingMode: attachmentFacingMode.value, width: { ideal: 1280 }, height: { ideal: 720 } } };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    attachmentStream.value = stream;
    if (attachmentVideoRef.value) {
      attachmentVideoRef.value.srcObject = stream;
      attachmentVideoRef.value.onloadedmetadata = () => attachmentVideoRef.value.play().catch(console.error);
    }
  } catch (err) {
    console.error("Attachment camera error:", err);
    toast.error("Could not access camera for attachments.");
  }
}

function stopAttachmentCamera() {
  if (attachmentStream.value) {
    attachmentStream.value.getTracks().forEach(track => track.stop());
    attachmentStream.value = null;
  }
  if (attachmentVideoRef.value) attachmentVideoRef.value.srcObject = null;
}

async function switchAttachmentCamera() {
  attachmentFacingMode.value = attachmentFacingMode.value === 'environment' ? 'user' : 'environment';
  await startAttachmentCamera();
}

function captureAttachmentPhoto() {
  if (!attachmentVideoRef.value) return;
  const canvas = document.createElement('canvas');
  canvas.width = attachmentVideoRef.value.videoWidth || 1280;
  canvas.height = attachmentVideoRef.value.videoHeight || 720;
  canvas.getContext('2d').drawImage(attachmentVideoRef.value, 0, 0, canvas.width, canvas.height);
  
  attachmentPhotos.value.push(canvas.toDataURL('image/jpeg', 0.92));
  canvas.toBlob((blob) => {
    if (blob) {
      const file = new File([blob], `attachment_${Date.now()}.jpg`, { type: 'image/jpeg' });
      attachmentPhotoFiles.value.push(file);
      newApplicant.value.attachment_photo_files.push(file);
    }
  }, 'image/jpeg', 0.92);
}

function removeAttachmentPhoto(index) {
  if (index >= 0 && index < attachmentPhotos.value.length) {
    attachmentPhotos.value.splice(index, 1);
    attachmentPhotoFiles.value.splice(index, 1);
    newApplicant.value.attachment_photo_files.splice(index, 1);
  }
}

watch(() => newApplicant.value?.attachment_type, (newVal) => {
  if (newVal === 'photo') startAttachmentCamera();
  else { stopAttachmentCamera(); attachmentPhotos.value = []; attachmentPhotoFiles.value = []; }
});
// ---------------------------------------

const editApplicant = (applicant) => {
  showAddForm.value = true;
  isEditMode.value = true;
  editingApplicantId.value = applicant.id;
  
  newApplicant.value = {
    id: applicant.id, name: applicant.name || '', email: applicant.email || '',
    phone: applicant.phone || '', position_id: applicant.position_id || '',
    status: applicant.status || '', entry_status: applicant.entry_status || '',
    period: applicant.period || '', recommendation: applicant.recommendation || '',
    agency_id: applicant.agency_id || '', inward_id: applicant.inward_id || '',
    file_type: applicant.cv_type || 'file', cv: null, cv_photos: [], cv_photo_files: [],
    comment: applicant.comment || '', interview_date: applicant.interview_date || '',
    interview_time: applicant.interview_time || '', attachment_type: '',
    attachment_files: [], attachment_photo_files: []
  };
  
  if (applicant.recommendation === 'inward' && applicant.inward_id) {
    const staff = staffMembers.value.find(s => s.id == applicant.inward_id);
    if (staff) { selectedStaff.value = staff; newApplicant.value.inward_id = staff.id; }
  }

  if (applicant.cv_type === 'photo' && applicant.cv_photo_paths?.length > 0) {
    newApplicant.value.file_type = 'photo';
    capturedPhotos.value = applicant.cv_photo_paths.map(path => path.startsWith('http') ? path : backendURL + path);
  }
  
  // 📎 Handle EXISTING attachments (for display only)
  if (applicant.attachments && applicant.attachments.length > 0) {
    existingAttachments.value = applicant.attachments.map(att => ({
      ...att,
      file_path: att.file_path.startsWith('http') ? att.file_path : backendURL + att.file_path
    }));
    const hasPhotos = applicant.attachments.some(a => a.file_type === 'photo');
    newApplicant.value.attachment_type = hasPhotos ? 'photo' : 'file';
  } else {
    existingAttachments.value = [];
  }
  
  setTimeout(() => { document.querySelector('.space-y-8')?.scrollIntoView({ behavior: 'smooth' }); }, 100);
};

const cancelEdit = () => {
  isEditMode.value = false;
  editingApplicantId.value = null;
  resetForm();
  showAddForm.value = false;
};

const isLoading = ref(false);
const addApplicant = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  const loadingMessage = isEditMode.value ? "Updating applicant..." : "Saving applicant...";
  let loadingToastId = toast.info(loadingMessage, { timeout: false });

  try {
    const formData = new FormData();
    formData.append("name", newApplicant.value.name);
    formData.append("email", newApplicant.value.email);
    formData.append("phone", newApplicant.value.phone);
    formData.append("position_id", newApplicant.value.position_id);
    formData.append("status", newApplicant.value.status);
    formData.append("entry_status", newApplicant.value.entry_status);
    formData.append("period", newApplicant.value.period);

    let recommendation_type = newApplicant.value.recommendation;
    let recommendation_id = null;
    let recommendation_text = null;

    if (recommendation_type === "agency") recommendation_id = newApplicant.value.agency_id;
    else if (recommendation_type === "inward") recommendation_id = newApplicant.value.inward_id || (selectedStaff?.value?.id || null);
    else if (recommendation_type === "other") recommendation_text = newApplicant.value.comment;

    formData.append("recommendation_type", recommendation_type || "");
    formData.append("recommendation_id", recommendation_id ?? "");
    formData.append("recommendation_text", recommendation_text ?? "");
    formData.append("comment", newApplicant.value.comment || "");
    formData.append("cv_type", newApplicant.value.file_type || "file");

    if (newApplicant.value.entry_status === 'New') {
      formData.append("interview_date", newApplicant.value.interview_date || "");
      formData.append("interview_time", newApplicant.value.interview_time || "");
    }

    if (newApplicant.value.file_type === 'file' && newApplicant.value.cv) {
      formData.append("cv", newApplicant.value.cv);
    } else if (newApplicant.value.file_type === 'photo' && newApplicant.value.cv_photo_files?.length > 0) {
      newApplicant.value.cv_photo_files.forEach((file, index) => formData.append(`cv_photos[${index}]`, file));
    }

    // Attachment handling
    formData.append("attachment_type", newApplicant.value.attachment_type || "");
    if (newApplicant.value.attachment_type === 'file' && newApplicant.value.attachment_files?.length > 0) {
      newApplicant.value.attachment_files.forEach((file, index) => formData.append(`attachment_files[${index}]`, file));
    } else if (newApplicant.value.attachment_type === 'photo' && attachmentPhotoFiles.value?.length > 0) {
      attachmentPhotoFiles.value.forEach((file, index) => formData.append(`attachment_photos[${index}]`, file));
    }

    let response;
    if (isEditMode.value && editingApplicantId.value) {
      formData.append("id", editingApplicantId.value);
      formData.append("_method", "PUT");
      
      if (existingAttachments.value?.length > 0) {
        existingAttachments.value.forEach((att, index) => {
          formData.append(`existing_attachment_ids[${index}]`, att.id);
        });
      }
      
      response = await api.post("Applicants.php", formData);
      if (!response.data.success) throw new Error(response.data.message || "Failed to update");
      toast.success("Applicant updated successfully!");
    } else {
      response = await api.post("Applicants.php", formData);
      if (!response.data.id) throw new Error(response.data.message || "Failed to create");
      toast.success("Applicant added successfully!");
    }

    resetForm();
    isEditMode.value = false;
    editingApplicantId.value = null;
    showAddForm.value = false;
    fetchApplicants();

  } catch (error) {
    toast.error(error.response?.data?.message || error.message || "An unexpected error occurred");
  } finally {
    isLoading.value = false;
    toast.dismiss(loadingToastId);
  }
};

const resetForm = () => {
  newApplicant.value = { name: '', position_id: '', email: '', phone: '', status: '', entry_status: '', period: '', recommendation: '', agency_id: '', inward_id: '', file_type: '', cv: null, cv_photos: [], cv_photo_files: [], comment: '', interview_date: '', interview_time: '', attachment_type: '', attachment_files: [], attachment_photo_files: [] };
  selectedStaff.value = null;
  capturedPhotos.value = [];
  attachmentPhotos.value = [];
  attachmentPhotoFiles.value = [];
  existingAttachments.value = [];
};

const openActionModal = (applicant, status) => {
  selectedApplicant.value = { ...applicant };
  actionStatus.value = status;
  showActionModal.value = true;
};

const updateApplicantStatus = async ({ applicant, status, comment }) => {
  const toastId = toast.info("Updating status...", { timeout: false });
  try {
    const res = await api.put("Applicants.php", { id: applicant.id, status, comment });
    if (res.data?.status === "error") throw new Error(res.data.message || "Failed to update status");
    
    const index = applicants.value.findIndex(a => a.id === applicant.id);
    if (index !== -1) {
      applicants.value[index] = { ...applicants.value[index], status, comment };
    }
    toast.success("Status updated successfully!");
    showActionModal.value = false;
    selectedApplicant.value = null;
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || "Failed to update status");
  } finally {
    toast.dismiss(toastId);
  }
};

const deleteApplicant = async (applicant) => {
  if (!confirm(`Are you sure you want to delete ${applicant.name}? This cannot be undone.`)) return;
  try {
    const res = await api.post('DeleteApplicant.php', { applicant_id: applicant.id });
    if (res.data.success) {
      applicants.value = applicants.value.filter(a => a.id !== applicant.id);
      toast.success("Applicant deleted successfully");
    } else {
      toast.error(res.data.message || 'Failed to delete applicant');
    }
  } catch (err) {
    toast.error('An error occurred while deleting the applicant');
  }
};

// --- CV Camera Functions ---
const removePhoto = (index) => {
  capturedPhotos.value.splice(index, 1);
  newApplicant.value.cv_photo_files.splice(index, 1);
};

async function startCamera() {
  stopCamera();
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facingMode.value, width: { ideal: 1280 }, height: { ideal: 720 } } });
    currentStream.value = stream;
    if (videoRef.value) {
      videoRef.value.srcObject = stream;
      videoRef.value.onloadedmetadata = () => videoRef.value.play().catch(console.error);
    }
  } catch (err) {
    console.error("Camera error:", err);
    toast.error("Could not access camera. Please check permissions.");
  }
}

function stopCamera() {
  if (currentStream.value) {
    currentStream.value.getTracks().forEach(track => track.stop());
    currentStream.value = null;
  }
  if (videoRef.value) videoRef.value.srcObject = null;
}

async function switchCamera() {
  facingMode.value = facingMode.value === 'environment' ? 'user' : 'environment';
  await startCamera();
}

function capturePhoto() {
  if (!videoRef.value) return;
  const canvas = document.createElement('canvas');
  canvas.width = videoRef.value.videoWidth || 1280;
  canvas.height = videoRef.value.videoHeight || 720;
  canvas.getContext('2d').drawImage(videoRef.value, 0, 0, canvas.width, canvas.height);
  
  capturedPhotos.value.push(canvas.toDataURL('image/jpeg', 0.92));
  canvas.toBlob((blob) => {
    if (blob) newApplicant.value.cv_photo_files.push(new File([blob], `cv_photo_${Date.now()}.jpg`, { type: 'image/jpeg' }));
  }, 'image/jpeg', 0.92);
}

watch(() => newApplicant.value?.file_type, (newVal) => {
  if (newVal === 'photo') startCamera();
  else { stopCamera(); capturedPhotos.value = []; newApplicant.value.cv_photo_files = []; }
});

// --- Photos Modal ---
const viewPhotos = (applicant) => { modalApplicant.value = applicant; showPhotosModal.value = true; };
const viewFullImage = (photoUrl) => { fullImage.value = photoUrl.startsWith('http') ? photoUrl : backendURL + photoUrl; };
const closePhotosModal = () => { showPhotosModal.value = false; modalApplicant.value = null; };
const closeFullImage = () => { fullImage.value = null; };

// --- Lifecycle ---
onMounted(() => {
  fetchPositions(); fetchApplicants(); fetchAgencies(); fetchInward();
  document.addEventListener('click', handleClickOutside);
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true;
    document.documentElement.classList.add('dark');
  }
});

onUnmounted(() => {
  stopCamera();
  stopAttachmentCamera();
  document.removeEventListener('click', handleClickOutside);
});

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) { document.documentElement.classList.add('dark'); localStorage.theme = 'dark'; } 
  else { document.documentElement.classList.remove('dark'); localStorage.theme = 'light'; }
};
</script>

<style>
.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.3s ease; }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateY(-10px); }
</style>